import React, {
  useState,
  useCallback,
  useEffect,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  sendRegistrationOTP,
  verifyRegistrationOTP,
} from '../services/api';
import { useApiMutation } from '../hooks/useApiQuery';

const RegisterUser = () => {
  const navigate = useNavigate();

  // step = 1 → collect details
  // step = 2 → enter OTP
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // =====================================================
  // STEP 1 — Send OTP
  // =====================================================
  const sendOtpMutation = useApiMutation(
    (data) => sendRegistrationOTP(data).then((res) => res.data),
    {
      onSuccess: () => {
        setError('');
        setSuccess('OTP sent to your email');
        setStep(2);
        setResendCooldown(30);
      },
      onError: (error) => {
        setSuccess('');
        setError(
          error.response?.data?.message || 'Failed to send OTP'
        );
      },
    }
  );

  // =====================================================
  // STEP 2 — Verify OTP and register
  // =====================================================
  const verifyOtpMutation = useApiMutation(
    (data) => verifyRegistrationOTP(data).then((res) => res.data),
    {
      onSuccess: (data) => {
        setError('');
        setSuccess('Registration successful! Redirecting...');

        // Auto-login
        if (data.token) {
          sessionStorage.setItem('token', data.token);
        }
        if (data.user) {
          sessionStorage.setItem('user', JSON.stringify(data.user));
        }

        setTimeout(() => {
          navigate(
            data.user?.role === 'user'
              ? '/attendee-dashboard'
              : '/dashboard'
          );
        }, 1200);
      },
      onError: (error) => {
        setSuccess('');
        setError(
          error.response?.data?.message || 'Invalid OTP'
        );
      },
    }
  );

  // =====================================================
  // Countdown for resend
  // =====================================================
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => {
      setResendCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // =====================================================
  // Handlers
  // =====================================================

  const handleChange = useCallback((e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    if (error) setError('');
  }, [error]);

  const handleOtpChange = useCallback((e) => {
    // Only digits, max 6
    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(digits);
    if (error) setError('');
  }, [error]);

  // Step 1 submit
  const handleSendOtp = useCallback(
    (e) => {
      e.preventDefault();
      setError('');
      setSuccess('');

      if (!formData.name.trim()) {
        setError('Name is required');
        return;
      }
      if (!formData.email.trim()) {
        setError('Email is required');
        return;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }

      sendOtpMutation.mutate({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
      });
    },
    [formData, sendOtpMutation]
  );

  // Step 2 submit
  const handleVerifyOtp = useCallback(
    (e) => {
      e.preventDefault();
      setError('');
      setSuccess('');

      if (otp.length !== 6) {
        setError('Enter the 6-digit code');
        return;
      }

      verifyOtpMutation.mutate({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        otp,
      });
    },
    [otp, formData, verifyOtpMutation]
  );

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0) return;

    try {
      await sendRegistrationOTP({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
      });
      setResendCooldown(30);
      setSuccess('OTP resent to your email');
      setError('');
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to resend OTP'
      );
    }
  }, [resendCooldown, formData]);

  const handleBackToDetails = useCallback(() => {
    setStep(1);
    setOtp('');
    setError('');
    setSuccess('');
  }, []);

  const isSending = sendOtpMutation.isPending;
  const isVerifying = verifyOtpMutation.isPending;
  const isLoading = isSending || isVerifying;

  // =====================================================
  // UI
  // =====================================================
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl border border-gray-100 shadow-sm p-8">
        {/* Logo */}
        <div className="text-center mb-6">
          <img
            src="https://res.cloudinary.com/bvpzaizf/image/upload/f_auto,q_auto,w_300,c_limit/v1787143638/logo2.png"
            alt="NFCfied Logo"
            className="h-14 mx-auto mb-3"
          />
          <p className="text-sm text-gray-500">
            {step === 1
              ? 'Create a new account'
              : 'Verify your email'}
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-3 rounded-md bg-red-50 border border-red-100 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="mb-6 p-3 rounded-md bg-[#1672b9]/10 border border-[#1672b9]/20 text-[#1672b9] text-sm">
            {success}
          </div>
        )}

        {/* =================================================
            STEP 1 — Details form
        ================================================= */}
        {step === 1 && (
          <form onSubmit={handleSendOtp} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                placeholder="John Doe"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                placeholder="you@example.com"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                placeholder="••••••••"
                required
                minLength={6}
                disabled={isLoading}
              />
              <p className="text-xs text-gray-400 mt-1">
                At least 6 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#1672b9] text-white py-2.5 rounded-lg text-sm font-medium shadow-sm hover:bg-[#0f5a9e] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSending ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Sending OTP...
                </>
              ) : (
                'Continue'
              )}
            </button>
          </form>
        )}

        {/* =================================================
            STEP 2 — OTP input
        ================================================= */}
        {step === 2 && (
          <form onSubmit={handleVerifyOtp} className="space-y-5">
            <div className="text-center">
              <p className="text-sm text-gray-600">
                We sent a 6-digit code to
              </p>
              <p className="text-sm font-medium text-gray-900 mt-1">
                {formData.email}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-center">
                Enter OTP
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={handleOtpChange}
                className="w-full text-center text-2xl tracking-[0.5em] font-mono border border-gray-200 rounded-lg py-3 focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] transition-all"
                placeholder="000000"
                maxLength={6}
                autoFocus
                disabled={isVerifying}
              />
            </div>

            <button
              type="submit"
              disabled={isVerifying || otp.length !== 6}
              className="w-full bg-[#1672b9] text-white py-2.5 rounded-lg text-sm font-medium shadow-sm hover:bg-[#0f5a9e] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isVerifying ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Verifying...
                </>
              ) : (
                'Verify & Create Account'
              )}
            </button>

            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={handleBackToDetails}
                disabled={isVerifying}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                ← Change details
              </button>

              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0 || isVerifying}
                className="text-[#1672b9] font-medium hover:underline disabled:text-gray-400 disabled:no-underline"
              >
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : 'Resend OTP'}
              </button>
            </div>
          </form>
        )}

        {/* Login Link */}
        <div className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-[#1672b9] font-medium hover:underline"
          >
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterUser;